// storePanel.ts — the STORE / INFINITE TOPICS surface (Tactical OLED redesign).
// This surface SHIPS DARK on purpose: there is NO purchase path and NO network call. The
// "START FREE TRIAL" CTA is rendered visibly DISABLED (dimmed, no handler), the top banner states
// monetization is off until compliance gates pass, and the footer "links" are inert text. Pure UI
// over modal() + the kit + tokens; no Core/data/network touch. Keep it that way.
import Phaser from 'phaser';
import { PALETTE, FONT, hex } from './keys';
import { modal } from './ui';
import { ctaButton, headingText } from './ui/kit';
import { scaledFont, motionAllowed } from './settings';
import { icon } from './icons';

/** Stroke a dashed rounded-rect border (Phaser has no native dash). Straight edges = dashes,
 *  corners = solid quarter-arcs. Coords are the box top-left in the caller's local space. */
function dashedRoundedRect(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  color: number,
  alpha: number,
  dash = 9,
  gap = 6,
  lw = 1.5,
): void {
  g.lineStyle(lw, color, alpha);
  const step = dash + gap;
  for (let dx = x + r; dx < x + w - r; dx += step) g.lineBetween(dx, y, Math.min(dx + dash, x + w - r), y);
  for (let dx = x + r; dx < x + w - r; dx += step) g.lineBetween(dx, y + h, Math.min(dx + dash, x + w - r), y + h);
  for (let dy = y + r; dy < y + h - r; dy += step) g.lineBetween(x, dy, x, Math.min(dy + dash, y + h - r));
  for (let dy = y + r; dy < y + h - r; dy += step) g.lineBetween(x + w, dy, x + w, Math.min(dy + dash, y + h - r));
  g.beginPath(); g.arc(x + r, y + r, r, Math.PI, Math.PI * 1.5); g.strokePath();
  g.beginPath(); g.arc(x + w - r, y + r, r, Math.PI * 1.5, Math.PI * 2); g.strokePath();
  g.beginPath(); g.arc(x + w - r, y + h - r, r, 0, Math.PI * 0.5); g.strokePath();
  g.beginPath(); g.arc(x + r, y + h - r, r, Math.PI * 0.5, Math.PI); g.strokePath();
}

export function openStore(scene: Phaser.Scene): ReturnType<typeof modal> {
  const m = modal(scene, { title: 'STORE', fullBleed: true, railGrad: [PALETTE.gold, PALETTE.armed] });
  const { card, cardW, bodyTop } = m;
  const innerW = cardW - 52;

  // ── Preview banner (dashed amber border, amber@0.1 wash) — the "ships disabled" disclosure ──
  const bannerY = bodyTop + 40;
  const bh = 64;
  const banner = scene.add.container(0, bannerY);
  const bannerBg = scene.add.graphics();
  bannerBg.fillStyle(PALETTE.armed, 0.1);
  bannerBg.fillRoundedRect(-innerW / 2, -bh / 2, innerW, bh, 12);
  dashedRoundedRect(bannerBg, -innerW / 2, -bh / 2, innerW, bh, 12, PALETTE.armed, 0.7);
  banner.add(bannerBg);
  banner.add(icon(scene, 'eyeOff', -innerW / 2 + 24, 0, 18, PALETTE.armed));
  banner.add(
    scene.add
      .text(-innerW / 2 + 44, 0, 'Preview — monetization ships disabled until compliance gates pass.', {
        fontFamily: FONT.family,
        fontSize: `${scaledFont(13, 11)}px`,
        color: hex(PALETTE.armed),
        lineSpacing: 3,
        wordWrap: { width: innerW - 64 },
      })
      .setOrigin(0, 0.5),
  );
  card.add(banner);

  // ── Hero: gold ∞ badge with a rewarding shimmer (#60) + title + subline ──
  const heroY = bodyTop + 152;
  // Soft gold bloom behind the badge — always on (reduceMotion-safe), so the premium unlock GLOWS
  // instead of reading as a flat glyph.
  const glow = scene.add.graphics({ x: 0, y: heroY });
  for (let i = 5; i >= 1; i -= 1) {
    glow.fillStyle(PALETTE.gold, 0.045 * i);
    const p = i * 7;
    glow.fillRoundedRect(-30 - p, -30 - p, 60 + p * 2, 60 + p * 2, 16 + i * 3);
  }
  card.add(glow);
  // Badge + top gloss + ∞ glyph grouped so they pulse as ONE unit around the badge centre.
  const badgeBox = scene.add.container(0, heroY);
  const badge = scene.add.graphics();
  badge.fillStyle(PALETTE.gold, 1);
  badge.fillRoundedRect(-30, -30, 60, 60, 16);
  const gloss = scene.add.graphics();
  gloss.fillStyle(0xffffff, 1);
  gloss.fillRoundedRect(-30, -30, 60, 28, { tl: 16, tr: 16, bl: 0, br: 0 });
  gloss.setAlpha(0.14);
  badgeBox.add([badge, gloss, icon(scene, 'infinity', 0, 0, 34, PALETTE.ink)]);
  card.add(badgeBox);
  // A twinkling sparkle accent at the badge's upper-right — the "reward" flourish (static under reduceMotion).
  const spark = icon(scene, 'sparkle', 26, heroY - 24, 15, PALETTE.gold);
  card.add(spark);
  if (motionAllowed()) {
    scene.tweens.add({ targets: badgeBox, scaleX: 1.05, scaleY: 1.05, duration: 1500, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    scene.tweens.add({ targets: gloss, alpha: 0.3, duration: 1100, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    scene.tweens.add({ targets: spark, scale: 1.25, alpha: 0.55, duration: 850, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
  }

  card.add(headingText(scene, 0, heroY + 64, 'INFINITE TOPICS', { size: 30, color: PALETTE.text, origin: 0.5, weight: 'bold' }));
  card.add(
    scene.add
      .text(0, heroY + 100, 'Unlimited boards on any topic — no daily caps.', {
        fontFamily: FONT.family,
        fontSize: `${scaledFont(15, 12)}px`,
        color: hex(PALETTE.dim),
        align: 'center',
        wordWrap: { width: innerW },
      })
      .setOrigin(0.5),
  );

  // ── Benefit list (green checkCircle + copy) ──
  const benefits = [
    'Unlimited custom & daily boards',
    'No waits, priority board cooking',
    'Support ongoing development',
    'The core game stays 100% free',
  ];
  const iconX = -cardW / 2 + 30;
  const textX = -cardW / 2 + 56;
  let by = heroY + 156;
  benefits.forEach((label) => {
    card.add(icon(scene, 'checkCircle', iconX, by, 20, PALETTE.correct));
    card.add(
      scene.add
        .text(textX, by, label, { fontFamily: FONT.family, fontSize: `${scaledFont(16, 12)}px`, color: hex(PALETTE.text) })
        .setOrigin(0, 0.5),
    );
    by += 46;
  });

  // ── Price card (gold border) ──
  const priceY = by + 56;
  const pcH = 92;
  const priceCard = scene.add.container(0, priceY);
  const pcBg = scene.add.graphics();
  pcBg.fillStyle(PALETTE.surface, 1);
  pcBg.fillRoundedRect(-innerW / 2, -pcH / 2, innerW, pcH, 14);
  pcBg.lineStyle(1.5, PALETTE.gold, 0.85);
  pcBg.strokeRoundedRect(-innerW / 2, -pcH / 2, innerW, pcH, 14);
  priceCard.add(pcBg);
  // "$5.99" (gold, large) + " / month" (dim) — centered as a group
  const priceVal = scene.add
    .text(0, -12, '$5.99', { fontFamily: FONT.display, fontSize: `${scaledFont(36, 22)}px`, color: hex(PALETTE.gold), fontStyle: 'bold' })
    .setOrigin(0, 0.5);
  const priceUnit = scene.add
    .text(0, -8, ' / month', { fontFamily: FONT.family, fontSize: `${scaledFont(15, 12)}px`, color: hex(PALETTE.dim) })
    .setOrigin(0, 0.5);
  const groupW = priceVal.width + priceUnit.width;
  priceVal.setX(-groupW / 2);
  priceUnit.setX(-groupW / 2 + priceVal.width);
  priceCard.add([priceVal, priceUnit]);
  priceCard.add(
    scene.add
      .text(0, 24, '7-day free trial · cancel anytime', {
        fontFamily: FONT.family,
        fontSize: `${scaledFont(12.5, 11)}px`,
        color: hex(PALETTE.dim),
      })
      .setOrigin(0.5),
  );
  card.add(priceCard);

  // ── Disabled CTA — gold fill, dimmed, NO handler, NO interactivity (ships dark) ──
  const ctaY = priceY + pcH / 2 + 50;
  const cta = ctaButton(scene, 0, ctaY, 'START FREE TRIAL', () => {}, {
    width: innerW,
    height: 60,
    fill: PALETTE.gold,
    glow: null,
  });
  cta.setAlpha(0.55);
  // strip interactivity so the disabled button has no hover/press/click path
  cta.list.slice().forEach((child) => {
    const obj = child as Phaser.GameObjects.GameObject & { input?: unknown };
    if (obj.input) obj.removeInteractive();
  });
  card.add(cta);

  // ── Footer: inert legal/account links (no network path) ──
  card.add(
    scene.add
      .text(0, ctaY + 50, 'Restore   ·   Privacy   ·   Terms', {
        fontFamily: FONT.family,
        fontSize: `${scaledFont(12.5, 11)}px`,
        color: hex(PALETTE.mute),
      })
      .setOrigin(0.5),
  );

  return m;
}
